import { DollarSign, Truck, Palette, Package, PenSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { createFrontTemplate, createBackTemplate } from '../lib/templates';
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { TemplatePreview } from './TemplatePreview';
import { SubscriptionTiers } from './SubscriptionTiers';
import { createCheckoutSession } from '../lib/stripe';
import { AlertCircle } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import { debug, info, error as logError, warn, network } from '../lib/logger';

interface CardEditorProps {
  cardImage: string;
  message: string;
  recipientName: string;
  occasion: string;
  signatureUrl?: string | null;
  onApprove: (data: { front: string; back: string; to: any }) => void;
  onRequestChanges: () => void;
  onClose: () => void;
  recipientAddress?: {
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
    address_country?: string;
    contact_id?: string;
  };
  userId: string;
  subscriptionStatus?: any;  // Add this to accept subscription data from parent
  userCredits?: number;      // Add this to accept credit data from parent
}

// Add constants to match Lob template
const ASPECT_RATIO = 6.25 / 4.25;  // width/height ratio for 6.25" x 4.25" template

// Add constants for text customization
const FONT_OPTIONS = {
  default: 'Arial',
  serif: 'Times New Roman',
  mono: 'Courier New',
  cursive: 'Brush Script MT'
};

const FONT_SIZES = {
  small: '12px',
  medium: '14px',
  large: '16px'
};

type Step = 'customize' | 'preview';

// Utility to check if a string is a valid URL
function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch (e) {
    return false;
  }
}

export function CardEditor({
  cardImage,
  message: initialMessage,
  recipientName,
  occasion: initialOccasion,
  signatureUrl: initialSignatureUrl,
  onApprove,
  onRequestChanges,
  onClose,
  recipientAddress,
  userId,
  subscriptionStatus,
  userCredits
}: CardEditorProps) {
  const [currentStep, setCurrentStep] = useState<Step>('customize');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(initialSignatureUrl || null);
  
  // Text customization state
  const [message, setMessage] = useState(initialMessage);
  const [textAlignment, setTextAlignment] = useState<'left' | 'center' | 'right'>('left');
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [fontFamily, setFontFamily] = useState<keyof typeof FONT_OPTIONS>('default');
  const [redirectTimer, setRedirectTimer] = useState<NodeJS.Timeout | null>(null);
  const [freeCardsAvailable, setFreeCardsAvailable] = useState(0);
  const [showSubscriptionTiers, setShowSubscriptionTiers] = useState(false);
  const [cardsUsedThisMonth, setCardsUsedThisMonth] = useState(0);
  const [subscription, setSubscription] = useState<any>(null);
  const [singleCardPrice, setSingleCardPrice] = useState<{ id: string; amount: number } | null>(null);

  // Add state variables for payment options
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);

  // Add proper types for pendingCardData
  interface PendingCardData {
    recipientName: string;
    recipientAddress?: {
      address_line1?: string;
      address_city?: string;
      address_state?: string;
      address_zip?: string;
      address_country?: string;
      contact_id?: string;
    };
    cardImage: string;
    message: string;
    occasion: string;
    textStyle: {
      font: string;
      alignment: string;
      fontSize: string;
    };
    signatureUrl?: string | null;
    timestamp: number;
    frontImageUrl?: string;
    backImageUrl?: string;
  }

  const hasAvailableCredits = freeCardsAvailable > cardsUsedThisMonth;
  const hasActiveSubscription = subscription?.status === 'active';
  const isCardFree = hasAvailableCredits || hasActiveSubscription;

  // Lob requires 4x6 postcards to be at least 1275px height × 1875px width (4.25in × 6.25in at 300dpi)
  const LOB_WIDTH = 1875;  // 6.25 inches * 300dpi
  const LOB_HEIGHT = 1275; // 4.25 inches * 300dpi
  
  // Create a full-sized rendering that matches Lob's requirements
  const renderToLobSizedImage = async (side: "front" | "back"): Promise<string> => {
    // Find the appropriate SVG element based on the side and step
    const previewId = side === "front" ? "lob-front-preview" : "lob-back-preview";
    const previewElement = document.getElementById(previewId);
    
    debug(`Rendering ${side} image at ${LOB_WIDTH}x${LOB_HEIGHT} pixels (Lob requirements for 4x6 postcard)`);
    
    if (!previewElement) {
      logError(`Could not find preview element with ID ${previewId}`);
      throw new Error(`Could not find preview element with ID ${previewId}`);
    }
    
    debug(`Found preview element with ID ${previewId}, looking for image or SVG inside`);
    
    // First check for an img element with a blob URL - this would be the rendered preview
    const imgElement = previewElement.querySelector("img[src^='blob:']") as HTMLImageElement;
    if (imgElement && imgElement.src) {
      debug(`Found img with blob URL in ${previewId}:`, imgElement.src);
      
      // Create a canvas to render the image at the required dimensions
      const canvas = document.createElement("canvas");
      canvas.width = LOB_WIDTH;
      canvas.height = LOB_HEIGHT;
      
      // Get canvas context
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Could not get canvas context");
      }
      
      // Fill with white background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Create an Image to render the blob URL
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          // Draw the image, ensuring it's properly centered and scaled
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to JPEG with high quality
          try {
            const jpeg = canvas.toDataURL("image/jpeg", 0.95);
            debug(`${side} image rendered successfully from blob URL. Size: ${jpeg.length} bytes, dimensions: ${canvas.width}x${canvas.height}`);
            resolve(jpeg);
          } catch (error) {
            logError(`Error converting ${side} blob to JPEG:`, error);
            reject(error);
          }
        };
        
        img.onerror = (error) => {
          logError(`Error loading ${side} blob to image:`, error);
          reject(new Error(`Error loading ${side} blob to image: ${error}`));
        };
        
        // Use the blob URL directly
        img.src = imgElement.src;
      });
    }
    
    // If no blob URL image is found, try looking for an SVG
    let svgElement = null;
    
    // First try direct selector
    svgElement = previewElement.querySelector("svg");
    
    // Try with data-testid
    if (!svgElement) {
      svgElement = previewElement.querySelector("[data-testid='template-preview'] svg");
    }
    
    // Try looking in the component
    if (!svgElement) {
      const componentId = `${previewId}-component`;
      const componentElement = document.getElementById(componentId);
      if (componentElement) {
        svgElement = componentElement.querySelector("svg");
      }
    }
    
    // Try looking in the container
    if (!svgElement) {
      const containerId = side === "front" ? "front-preview-container" : "back-preview-container";
      const containerElement = document.getElementById(containerId);
      if (containerElement) {
        svgElement = containerElement.querySelector("svg");
      }
    }
    
    // Try looking in any img tags for SVG data URLs
    if (!svgElement) {
      const imgElement = previewElement.querySelector("img[src^='data:image/svg+xml']");
      if (imgElement) {
        debug(`Found img with SVG data URL in ${previewId}`);
        // For img tags with SVG data URLs, we'll create a new SVG element
        const parser = new DOMParser();
        const imgSrc = (imgElement as HTMLImageElement).src;
        const svgString = atob(imgSrc.split(',')[1]);
        const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
        svgElement = svgDoc.documentElement;
      }
    }
    
    // Last resort - search entire document
    if (!svgElement) {
      svgElement = document.querySelector(`#${previewId} svg, [id*='${previewId}'] svg`);
    }
    
    if (!svgElement) {
      logError(`Could not find SVG element within ${previewId}`);
      logError('Preview element contents:', previewElement.innerHTML.substring(0, 500));
      
      // If we can't find an SVG, try the fallback method
      logError('Attempting to use fallback image generation for', side);
      return createFallbackImage(side).then(blob => {
        return URL.createObjectURL(blob);
      });
    }
    
    // Clone the SVG to avoid modifying the displayed one
    const clonedSvg = svgElement.cloneNode(true) as SVGElement;
    
    // Set dimensions for Lob (6.25 × 4.25 inches at 300dpi)
    clonedSvg.setAttribute("width", `${LOB_WIDTH}px`);  // 6.25 inches * 300dpi
    clonedSvg.setAttribute("height", `${LOB_HEIGHT}px`); // 4.25 inches * 300dpi
    clonedSvg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    
    // Create a serialized SVG string
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);
    
    // Create a canvas to render the SVG
    const canvas = document.createElement("canvas");
    canvas.width = LOB_WIDTH;  // 6.25 inches * 300dpi
    canvas.height = LOB_HEIGHT; // 4.25 inches * 300dpi
    
    // Get canvas context
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get canvas context");
    }
    
    // Fill with white background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Create an Image to render the SVG
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // Draw the image, ensuring it's properly centered and scaled
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to JPEG with high quality
        try {
          const jpeg = canvas.toDataURL("image/jpeg", 0.95);
          debug(`${side} image rendered successfully. Size: ${jpeg.length} bytes`);
          resolve(jpeg);
        } catch (error) {
          logError(`Error converting ${side} to JPEG:`, error);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        logError(`Error loading ${side} SVG to image:`, error);
        reject(new Error(`Error loading ${side} SVG to image: ${error}`));
      };
      
      // Convert the SVG string to a data URL
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      
      img.src = url;
      
      // Add a safety timeout in case the image never loads
      const timeoutId = setTimeout(() => {
        warn(`Loading ${side} SVG timed out, falling back to alternative method`);
        createFallbackImage(side).then(blob => {
          const fallbackUrl = URL.createObjectURL(blob);
          img.src = fallbackUrl;
        }).catch(err => {
          reject(err);
        });
      }, 5000); // 5 second timeout
      
      // Clean up the URL object and timeout when done
      img.onload = () => {
        clearTimeout(timeoutId);
        try {
          // Draw the image, ensuring it's properly centered and scaled
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to JPEG with high quality
          const jpeg = canvas.toDataURL("image/jpeg", 0.95);
          debug(`${side} image rendered successfully. Size: ${jpeg.length} bytes`);
          URL.revokeObjectURL(url); // Clean up the URL object
          resolve(jpeg);
        } catch (error) {
          logError(`Error converting ${side} to JPEG:`, error);
          URL.revokeObjectURL(url);
          reject(error);
        }
      };
    });
  };
  
  // Add a dedicated fallback function to create a clean image
  const createFallbackImage = (side: 'front' | 'back'): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        // Lob dimensions
        const LOB_WIDTH = 1875;  // 6.25 inches * 300dpi
        const LOB_HEIGHT = 1275; // 4.25 inches * 300dpi
        
        // Create a canvas at Lob dimensions - landscape orientation
        const canvas = document.createElement('canvas');
        canvas.width = LOB_WIDTH;
        canvas.height = LOB_HEIGHT;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not create canvas context'));
    return;
  }

        // Fill with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        if (side === 'front') {
          // For front, load the cardImage directly
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          
          img.onload = () => {
            // Draw image to fit the entire canvas
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to convert canvas to blob'));
    return;
  }
                debug(`Fallback: Created ${side} JPEG: ${canvas.width}×${canvas.height} pixels`);
                resolve(blob);
              },
              'image/jpeg',
              0.95
            );
          };
          
          img.onerror = () => {
            // If image fails to load, create a placeholder instead
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.font = '48px Arial';
            ctx.fillStyle = '#999999';
            ctx.textAlign = 'center';
            ctx.fillText('Card Image', canvas.width/2, canvas.height/2);
            
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to convert canvas to blob'));
    return;
  }
                debug(`Fallback: Created placeholder for ${side}`);
                resolve(blob);
              },
              'image/jpeg',
              0.95
            );
          };
          
          img.src = cardImage;
        } else {
          // For back, manually draw a simplified version
          // Draw message text
          ctx.font = `${parseInt(FONT_SIZES[fontSize].replace('px', '')) * 6}px ${FONT_OPTIONS[fontFamily]}`;
          ctx.fillStyle = '#000000';
          ctx.textAlign = textAlignment === 'center' ? 'center' : 
                         textAlignment === 'right' ? 'right' : 'left';
          
          // Position for message text
          const textX = textAlignment === 'center' ? canvas.width/2 : 
                       textAlignment === 'right' ? canvas.width - 300 : 300;
          const textY = 300;
          
          // Split message into lines
          const formattedMessage = message.replace(/\n/g, '|').split('|');
          const lineHeight = parseInt(FONT_SIZES[fontSize].replace('px', '')) * 7;
          
          formattedMessage.forEach((line, index) => {
            ctx.fillText(line, textX, textY + (index * lineHeight), canvas.width - 600);
          });
          
          // Draw recipient information
          if (recipientAddress) {
            ctx.font = '36px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(recipientName, canvas.width - 400, canvas.height - 300);
            
            if (recipientAddress.address_line1) {
              ctx.fillText(recipientAddress.address_line1, canvas.width - 400, canvas.height - 250);
            }
            
            const cityStateZip = [
              recipientAddress.address_city,
              recipientAddress.address_state,
              recipientAddress.address_zip
            ].filter(Boolean).join(', ');
            
            if (cityStateZip) {
              ctx.fillText(cityStateZip, canvas.width - 400, canvas.height - 200);
            }
          }
          
          // Draw postage area
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.strokeRect(canvas.width - 300, 150, 200, 200);
          ctx.font = '32px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('POSTAGE', canvas.width - 200, 230);
          ctx.fillText('INDICIA', canvas.width - 200, 270);
          
          // If there's a signature, try to add it
          if (signatureUrl) {
            const sig = new Image();
            sig.crossOrigin = 'Anonymous';
            sig.onload = () => {
              // Draw signature in bottom left
              ctx.drawImage(sig, 300, canvas.height - 400, 400, 200);
              
              // Convert to blob
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    reject(new Error('Failed to convert canvas to blob'));
    return;
  }
                  debug(`Fallback: Created ${side} JPEG with signature`);
                  resolve(blob);
                },
                'image/jpeg',
                0.95
              );
            };
            
            sig.onerror = () => {
              // If signature fails, still create the image without it
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    reject(new Error('Failed to convert canvas to blob'));
                    return;
                  }
                  debug(`Fallback: Created ${side} JPEG without signature`);
                  resolve(blob);
                },
                'image/jpeg',
                0.95
              );
            };
            
            sig.src = signatureUrl;
          } else {
            // No signature, just convert the canvas
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to convert canvas to blob'));
                  return;
                }
                debug(`Fallback: Created ${side} JPEG without signature`);
                resolve(blob);
              },
              'image/jpeg',
              0.95
            );
          }
        }
      } catch (error) {
        reject(new Error(`Fallback generation failed: ${error}`));
      }
    });
  };

  useEffect(() => {
    // Only fetch if subscription data wasn't passed as props
    if (!subscriptionStatus) {
      fetchSubscriptionStatus();
    } else {
      // Use the passed subscription data
      if (subscriptionStatus.subscriptionDetails) {
        setSubscription(subscriptionStatus.subscriptionDetails);
      }
      
      // Use the passed credits data
      if (userCredits !== undefined) {
        setFreeCardsAvailable(userCredits);
      }
    }
    
    fetchSingleCardPrice();
  }, [userId, subscriptionStatus, userCredits]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [redirectTimer]);

  async function fetchSubscriptionStatus() {
    // Skip API calls if props were passed
    if (subscriptionStatus && userCredits !== undefined) {
      if (subscriptionStatus.subscriptionDetails) {
        setSubscription(subscriptionStatus.subscriptionDetails);
      }
      setFreeCardsAvailable(userCredits);
      return;
    }
    
    try {
      // Original API call logic for when props aren't available
      // Get user's subscription status
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .rpc('get_user_subscription', { user_id: userId });

      if (subscriptionError) {
        logError('Error fetching subscription:', subscriptionError);
      }

      setSubscription(subscriptionData);

      // Get user's credits using RPC function
      const { data: creditsData, error: creditsError } = await supabase
        .rpc('get_user_credits_with_expiration');

      if (creditsError) {
        logError('Error fetching credits:', creditsError);
      }

      // Set the credits from the response
      if (creditsData && creditsData.length > 0) {
        setFreeCardsAvailable(creditsData[0].total_credits);
        // For now, we'll use total_credits - credits_remaining as used credits
        const usedCredits = creditsData[0].purchases.reduce((total: number, purchase: any) => {
          return total + (purchase.credits_amount - purchase.credits_remaining);
        }, 0);
        setCardsUsedThisMonth(usedCredits);
      }

      // Log the data for debugging
      debug('Subscription:', subscriptionData);
      debug('Credits:', creditsData);

    } catch (error) {
      logError('Error fetching subscription status:', error);
    }
  }

  async function fetchSingleCardPrice() {
    try {
      // Get the single card price using our RPC function
      const { data: priceData, error: priceError } = await supabase
        .rpc('get_single_card_price');

      if (priceError) {
        logError('Error fetching single card price:', priceError);
        // Try fallback query if the RPC function doesn't exist yet
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('stripe_prices_view')
          .select(`
            id,
            unit_amount,
            stripe_products!inner (
              name,
              attrs
            )
          `)
          .eq('active', true)
          .eq('stripe_products.name', 'Single Card')
          .contains('stripe_products.attrs', { metadata: { purchase_type: 'single_card' } })
          .eq('type', 'one_time')
          .order('created', { ascending: false })
          .limit(1);
        
        if (fallbackError) {
          logError('Error in fallback query:', fallbackError);
          return;
        }
        
        if (fallbackData && fallbackData.length > 0) {
          setSingleCardPrice({
            id: fallbackData[0].id,
            amount: fallbackData[0].unit_amount / 100 // Convert cents to dollars
          });
          debug('Single card price (fallback):', fallbackData[0]);
        }
        return;
      }

      if (priceData && priceData.length > 0) {
        setSingleCardPrice({
          id: priceData[0].id,
          amount: priceData[0].unit_amount / 100 // Convert cents to dollars
        });
        debug('Single card price (RPC):', priceData[0]);
      } else {
        logError('No single card price found');
      }
    } catch (error) {
      logError('Error fetching single card price:', error);
    }
  }

  // Helper function to format price
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Add a unified function for rendering and uploading card images
  const renderAndUploadCardImages = async (): Promise<{ frontImageUrl: string; backImageUrl: string }> => {
    debug('Starting unified card image rendering and upload process...');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');
      
      // Generate a timestamp for unique filenames
      const timestamp = Date.now();
      
      // Render both sides at precisely the dimensions Lob requires
      debug('Creating print-ready images at Lob dimensions...');
      
      // Generate high-quality images from the current state
      const [frontDataUrl, backDataUrl] = await Promise.all([
        renderToLobSizedImage('front'),
        renderToLobSizedImage('back')
      ]);
      
      // Convert data URLs to blobs
      const frontBlob = await fetch(frontDataUrl).then(res => res.blob());
      const backBlob = await fetch(backDataUrl).then(res => res.blob());
      
      // Update filenames to use jpg extension with timestamp for uniqueness
      const frontFileName = `templates/${session.user.id}-${timestamp}-front.jpg`;
      const backFileName = `templates/${session.user.id}-${timestamp}-back.jpg`;
      
      debug('Uploading rendered images to Supabase Storage...');
      
      // Upload to Supabase Storage
      const [frontUploadResult, backUploadResult] = await Promise.all([
        supabase.storage
          .from('cardible-templates')
          .upload(frontFileName, frontBlob, {
            contentType: 'image/jpeg',
            cacheControl: '3600',
            upsert: true
          }),
        supabase.storage
          .from('cardible-templates')
          .upload(backFileName, backBlob, {
            contentType: 'image/jpeg',
            cacheControl: '3600',
            upsert: true
          })
      ]);

      // Check for upload errors
      if (frontUploadResult.error) {
        throw new Error(`Error uploading front image: ${frontUploadResult.error.message}`);
      }
      
      if (backUploadResult.error) {
        throw new Error(`Error uploading back image: ${backUploadResult.error.message}`);
      }

      // Get public URLs for the uploaded files
      const frontImageUrl = supabase.storage
        .from('cardible-templates')
        .getPublicUrl(frontFileName).data.publicUrl;

      const backImageUrl = supabase.storage
        .from('cardible-templates')
        .getPublicUrl(backFileName).data.publicUrl;
      
      debug('Successfully uploaded card images:', { 
        front: frontImageUrl, 
        back: backImageUrl 
      });

      // Validate the URLs are properly formed image URLs
      if (!frontImageUrl.match(/\.(jpe?g|png|gif|webp)$/i)) {
        throw new Error('Front image URL does not have a valid image extension');
      }
      
      if (!backImageUrl.match(/\.(jpe?g|png|gif|webp)$/i)) {
        throw new Error('Back image URL does not have a valid image extension');
      }
      
      return { frontImageUrl, backImageUrl };
    } catch (error) {
      logError('Error in renderAndUploadCardImages:', error);
      throw error;
    }
  };

  // Update handleApproveWithCredits to use the new unified function
  const handleApproveWithCredits = async () => {
    setIsSubmitting(true);
    
    try {
      // Step 1: Pre-render and upload images
      debug('Pre-rendering and uploading card images for credit purchase...');
      const { frontImageUrl, backImageUrl } = await renderAndUploadCardImages();
      
      // Step 2: Format message with line breaks
      const formattedMessage = message.replace(/\n/g, '<br>');
      
      // Step 3: Pass the data to the parent component with the image URLs
      onApprove({
        front: frontImageUrl,
        back: backImageUrl,
        to: {
          name: recipientName,
          address_line1: recipientAddress?.address_line1 || '',
          address_city: recipientAddress?.address_city || '',
          address_state: recipientAddress?.address_state || '',
          address_zip: recipientAddress?.address_zip || '',
          address_country: recipientAddress?.address_country || 'US'
        }
      });
      
      setIsSubmitting(false);
    } catch (error) {
      logError('Error processing with credits:', error);
      setError(error instanceof Error ? error.message : 'Failed to process card');
      setIsSubmitting(false);
    }
  };

  const handleApproveWithSubscription = async () => {
    // Similar to handleApproveWithCredits
    await handleApproveWithCredits();
  };

  const handlePaymentFlow = async () => {
    setIsSubmitting(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');
      
      // Generate a timestamp
      const timestamp = Date.now();
      
      // Step 1: Pre-render and upload images
      debug('Pre-rendering and uploading card images for payment...');
      let frontImageUrl, backImageUrl;
      
      try {
        const result = await renderAndUploadCardImages();
        frontImageUrl = result.frontImageUrl;
        backImageUrl = result.backImageUrl;
      } catch (renderError) {
        logError('Error pre-rendering card images:', renderError);
        // We'll continue without image URLs and let the backend handle it
      }
      
      // Prepare card data for localStorage with correct types
      const pendingCardData: PendingCardData = {
        recipientName,
        recipientAddress,
        cardImage,
        message,
        occasion: initialOccasion,
        textStyle: {
          font: FONT_OPTIONS[fontFamily],
          alignment: textAlignment,
          fontSize: FONT_SIZES[fontSize]
        },
        signatureUrl,
        timestamp,
        // Include the pre-rendered image URLs if available
        frontImageUrl,
        backImageUrl
      };
      
      // Store pending card data in localStorage
      localStorage.setItem('cardible_pending_card', JSON.stringify(pendingCardData));
      
      // Redirect to Stripe checkout with retry logic for database errors
      if (singleCardPrice) {
        // Set up retry logic for Stripe checkout
        const maxRetries = 3;
        let retryCount = 0;
        
        while (retryCount <= maxRetries) {
          try {
            if (retryCount > 0) {
              debug(`Retry ${retryCount}/${maxRetries} for Stripe checkout...`);
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
            
            await createCheckoutSession('one-time', singleCardPrice.id);
            break; // Success, exit the loop
            
          } catch (checkoutError: any) {
            if (checkoutError.message && 
                (checkoutError.message.includes('503') || checkoutError.code === 'PGRST002')) {
              // Database unavailable, retry
              retryCount++;
              if (retryCount > maxRetries) {
                throw new Error('Database currently unavailable. Please try again in a few moments.');
              }
              continue;
            }
            // Other error, throw it
            throw checkoutError;
          }
        }
      } else {
        throw new Error('Card price not available');
      }
    } catch (error) {
      logError('Error starting payment flow:', error);
      setError(error instanceof Error ? error.message : 'Failed to start payment process');
      setIsSubmitting(false);
    }
  };

  const handleCloseAndRedirect = () => {
    // Clear any existing timer
    if (redirectTimer) {
      clearTimeout(redirectTimer);
      setRedirectTimer(null);
    }

    // Clean up local state
    setShowSuccess(false);
    setOrderData(null);
    
    // Close all modals and return to dashboard
    // We don't need to call onApprove again since the order is already created
    onClose();
  };

  const handleSubscribe = async (tier: string) => {
    try {
      const priceId = tier === 'basic' ? 'price_RjZ8s24Zn23gsp' :
                      tier === 'standard' ? 'price_RjVfomf8LFz4yA' :
                      'price_RjZCheZhAiiVpP';
      
      await createCheckoutSession('subscription', priceId);
      setShowSubscriptionTiers(false);
    } catch (error) {
      logError('Error subscribing:', error);
      // Handle error (show error message to user)
    }
  };

  const handleSignatureSave = async (dataUrl: string) => {
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Upload signature
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const fileExt = 'png';
      const fileName = `${session.user.id}-${Math.random()}.${fileExt}`;
      const filePath = `signatures/${fileName}`;

      // Upload to the existing cardible-templates bucket
      const { error: uploadError } = await supabase.storage
        .from('cardible-templates')
        .upload(filePath, blob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('cardible-templates')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('users')
        .update({ signature_url: publicUrl })
        .eq('user_id', session.user.id);

      if (updateError) throw updateError;

      setSignatureUrl(publicUrl);
      setShowSignaturePad(false);
    } catch (error) {
      logError('Error saving signature:', error);
      setError('Failed to save signature. Please try again.');
    }
  };

  // Helper function to render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'customize':
        return (
          <div className="flex flex-col h-full">
            {/* Preview Section - Fixed below header on both mobile and desktop */}
            <div className="flex-shrink-0 bg-gray-50 p-4">
              <div className="w-full max-w-[600px] mx-auto">
                <TemplatePreview
                  id="lob-back-preview-customize"
                  side="back"
                  message={message}
                  recipientName={recipientName}
                  recipientAddress={recipientAddress}
                  signatureUrl={signatureUrl}
                  occasion={initialOccasion}
                  textStyle={{
                    font: FONT_OPTIONS[fontFamily],
                    alignment: textAlignment,
                    fontSize: FONT_SIZES[fontSize]
                  }}
                />
              </div>
            </div>

            {/* Customization Options - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Your Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full p-3 border rounded-lg min-h-[150px] text-base focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    placeholder="Write your message here..."
                  />
                </div>

                {/* Signature */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Your Signature</label>
                  <div className="space-y-4">
                    {signatureUrl ? (
                      <div className="relative">
                        <img
                          src={signatureUrl}
                          alt="Your signature"
                          className="max-w-full h-auto max-h-20"
                        />
                        <button
                          onClick={() => setShowSignaturePad(true)}
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-50"
                        >
                          <PenSquare className="w-4 h-4 text-brand-secondary" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSignaturePad(true)}
                        className="w-full p-4 border-2 border-dashed rounded-lg text-brand-secondary hover:text-brand-primary hover:border-brand-primary transition-colors"
                      >
                        <PenSquare className="w-6 h-6 mx-auto mb-2" />
                        <span>Draw Your Signature</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Font Style */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Font Style</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(FONT_OPTIONS).map(([key, font]) => (
                      <button
                        key={key}
                        onClick={() => setFontFamily(key as keyof typeof FONT_OPTIONS)}
                        className={`p-3 border rounded-lg text-center transition-all ${
                          fontFamily === key 
                            ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                            : 'hover:border-brand-accent hover:bg-gray-50'
                        }`}
                        style={{ fontFamily: font }}
                      >
                        Aa
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Font Size</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(FONT_SIZES).map(([key, size]) => (
                      <button
                        key={key}
                        onClick={() => setFontSize(key as keyof typeof FONT_SIZES)}
                        className={`p-3 border rounded-lg text-center transition-all ${
                          fontSize === key 
                            ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                            : 'hover:border-brand-accent hover:bg-gray-50'
                        }`}
                        style={{ fontSize: size }}
                      >
                        Aa
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text Alignment */}
                <div>
                  <label className="block text-sm font-medium text-brand-secondary mb-2">Text Alignment</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['left', 'center', 'right'].map((align) => (
                      <button
                        key={align}
                        onClick={() => setTextAlignment(align as 'left' | 'center' | 'right')}
                        className={`p-3 border rounded-lg text-center transition-all ${
                          textAlignment === align 
                            ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                            : 'hover:border-brand-accent hover:bg-gray-50'
                        }`}
                      >
                        {align === 'left' && 'Left'}
                        {align === 'center' && 'Center'}
                        {align === 'right' && 'Right'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'preview':
        return (
          <div className="h-full flex flex-col">
            {/* Scrollable Container */}
            <div className="flex-1 overflow-y-auto">
              {/* Front Preview */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Front</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto" id="front-preview-container">
                    <TemplatePreview
                      id="lob-front-preview"
                      side="front"
                      cardImage={cardImage}
                      className="mx-auto"
                      showDebug={false}
                    />
                  </div>
                </div>
              </div>

              {/* Back Preview */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Back</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto" id="back-preview-container">
                    <TemplatePreview
                      id="lob-back-preview"
                      side="back"
                      message={message}
                      recipientName={recipientName}
                      recipientAddress={recipientAddress}
                      occasion={initialOccasion}
                      signatureUrl={signatureUrl}
                      showDebug={false}
                      textStyle={{
                        font: FONT_OPTIONS[fontFamily],
                        alignment: textAlignment,
                        fontSize: FONT_SIZES[fontSize]
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Cost Section */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Cost</h3>
                <div className="bg-white rounded-lg border p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-brand-secondary opacity-75" />
                        <span>One-time Purchase</span>
                      </div>
                      <span className={isCardFree ? 'line-through text-gray-400' : ''}>
                        {singleCardPrice ? formatPrice(singleCardPrice.amount) : '$3.50'}
                      </span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between items-center font-semibold">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-brand-primary" />
                          <span>Total</span>
                        </div>
                        <span className="text-brand-primary">
                          {isCardFree ? 'FREE' : (singleCardPrice ? formatPrice(singleCardPrice.amount) : '$3.50')}
                        </span>
                      </div>
                      {hasAvailableCredits && (
                        <p className="text-sm text-green-600 mt-2">
                          Using 1 credit from your available credits ({freeCardsAvailable - cardsUsedThisMonth} remaining)
                        </p>
                      )}
                      {hasActiveSubscription && !hasAvailableCredits && (
                        <p className="text-sm text-green-600 mt-2">
                          Free with your {subscription?.price_id === 'price_1Qq2ecPcJBAkjOG46gNS7Qo5' ? 'Standard' : 'Premium'} subscription
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  // Helper function to get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 'customize':
        return 'Customize Your Card';
      case 'preview':
        return 'Review Card';
    }
  };

  // Helper function to get action button text
  const getActionButtonText = () => {
    if (isSubmitting) return 'Sending...';
    switch (currentStep) {
      case 'customize':
        return 'Continue to Preview';
      case 'preview':
        return isCardFree ? 'Approve and Send Card' : 'Purchase and Send Card';
    }
  };

  // Helper function to handle next step
  const handleNextStep = () => {
    if (currentStep === 'preview') {
      handleApprove();
    } else {
      info('Switching to preview mode...');
      setCurrentStep('preview');
      
      // Log the state of preview elements after a delay
      setTimeout(() => {
        const frontPreview = document.getElementById('lob-front-preview');
        const backPreview = document.getElementById('lob-back-preview');
        
        debug('Front preview element found:', !!frontPreview);
        debug('Back preview element found:', !!backPreview);
        
        if (frontPreview) {
          debug('Front preview content:', frontPreview.innerHTML.substring(0, 100) + '...');
        }
        
        if (backPreview) {
          debug('Back preview content:', backPreview.innerHTML.substring(0, 100) + '...');
        }
      }, 1000);
    }
  };

  // Update the handleApprove function to use the new payment flow
  const handleApprove = async () => {
    setIsSubmitting(true);
    
    try {
      // Check if user has enough credits
      if (typeof userCredits === 'number' && userCredits > 0) {
        // User can use a credit
        handleApproveWithCredits();
      } else if (subscriptionStatus?.active) {
        // Check if user has cards remaining in their subscription
        if (subscriptionStatus.cardsRemaining > 0) {
          // User can send a card using their subscription
          handleApproveWithSubscription();
        } else {
          // No cards left in subscription, use payment flow
          handlePaymentFlow();
        }
      } else {
        // No credits or subscription, use payment flow
        handlePaymentFlow();
      }
    } catch (error) {
      logError('Error in approval flow:', error);
      setError(error instanceof Error ? error.message : 'Failed to process approval');
      setIsSubmitting(false);
    }
  };

  if (showSuccess && orderData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-md flex flex-col min-h-[300px]">
          {/* Success Content */}
          <div className="flex-1 p-6">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold font-display text-brand-secondary mb-2">
                Card Ordered Successfully!
              </h3>
              <p className="text-brand-secondary mb-6 text-center">
                Your {initialOccasion.toLowerCase()} card for {recipientName} has been ordered and will be delivered soon.
              </p>
              <div className="text-sm text-brand-secondary opacity-75">
                Returning to dashboard in 10 seconds...
              </div>
            </div>
          </div>

          {/* Action Button - Fixed at bottom */}
          <div className="flex-shrink-0 border-t bg-white p-4">
            <button
              onClick={handleCloseAndRedirect}
              className="w-full px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showSubscriptionTiers) {
    return (
      <SubscriptionTiers
        onClose={() => setShowSubscriptionTiers(false)}
        onSelectTier={handleSubscribe}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl flex flex-col h-[90vh]">
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold font-display text-brand-secondary">
            {getStepTitle()}
          </h2>
          <button 
            onClick={onClose}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message - if present */}
        {error && (
          <div className="flex-shrink-0 p-4 bg-red-50 border-b border-red-200 flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {currentStep === 'customize' && (
            <div className="flex flex-col h-full">
              {/* Preview Section - Fixed below header on both mobile and desktop */}
              <div className="flex-shrink-0 bg-gray-50 p-4">
                <div className="w-full max-w-[600px] mx-auto">
                  <TemplatePreview
                    id="lob-back-preview-customize"
                    side="back"
                    message={message}
                    recipientName={recipientName}
                    recipientAddress={recipientAddress}
                    signatureUrl={signatureUrl}
                    occasion={initialOccasion}
                    textStyle={{
                      font: FONT_OPTIONS[fontFamily],
                      alignment: textAlignment,
                      fontSize: FONT_SIZES[fontSize]
                    }}
                  />
                </div>
              </div>

              {/* Customization Options - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-3xl mx-auto space-y-6">
                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Your Message</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full p-3 border rounded-lg min-h-[150px] text-base focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      placeholder="Write your message here..."
                    />
                  </div>

                  {/* Signature */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Your Signature</label>
                    <div className="space-y-4">
                      {signatureUrl ? (
                        <div className="relative">
                          <img
                            src={signatureUrl}
                            alt="Your signature"
                            className="max-w-full h-auto max-h-20"
                          />
                          <button
                            onClick={() => setShowSignaturePad(true)}
                            className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-50"
                          >
                            <PenSquare className="w-4 h-4 text-brand-secondary" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowSignaturePad(true)}
                          className="w-full p-4 border-2 border-dashed rounded-lg text-brand-secondary hover:text-brand-primary hover:border-brand-primary transition-colors"
                        >
                          <PenSquare className="w-6 h-6 mx-auto mb-2" />
                          <span>Draw Your Signature</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Font Style */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Font Style</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(FONT_OPTIONS).map(([key, font]) => (
                        <button
                          key={key}
                          onClick={() => setFontFamily(key as keyof typeof FONT_OPTIONS)}
                          className={`p-3 border rounded-lg text-center transition-all ${
                            fontFamily === key 
                              ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                              : 'hover:border-brand-accent hover:bg-gray-50'
                          }`}
                          style={{ fontFamily: font }}
                        >
                          Aa
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font Size */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Font Size</label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(FONT_SIZES).map(([key, size]) => (
                        <button
                          key={key}
                          onClick={() => setFontSize(key as keyof typeof FONT_SIZES)}
                          className={`p-3 border rounded-lg text-center transition-all ${
                            fontSize === key 
                              ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                              : 'hover:border-brand-accent hover:bg-gray-50'
                          }`}
                          style={{ fontSize: size }}
                        >
                          Aa
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Text Alignment */}
                  <div>
                    <label className="block text-sm font-medium text-brand-secondary mb-2">Text Alignment</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['left', 'center', 'right'].map((align) => (
                        <button
                          key={align}
                          onClick={() => setTextAlignment(align as 'left' | 'center' | 'right')}
                          className={`p-3 border rounded-lg text-center transition-all ${
                            textAlignment === align 
                              ? 'border-brand-primary text-brand-primary bg-brand-accent' 
                              : 'hover:border-brand-accent hover:bg-gray-50'
                          }`}
                        >
                          {align === 'left' && 'Left'}
                          {align === 'center' && 'Center'}
                          {align === 'right' && 'Right'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'preview' && (
            <div className="p-4 space-y-6">
              {/* Front Preview */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Front</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto">
                    <TemplatePreview
                      id="lob-front-preview"
                      side="front"
                      cardImage={cardImage}
                      className="mx-auto"
                      showDebug={false}
                    />
                  </div>
                </div>
              </div>

              {/* Back Preview */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Back</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="w-full max-w-[600px] mx-auto">
                    <TemplatePreview
                      id="lob-back-preview"
                      side="back"
                      message={message}
                      recipientName={recipientName}
                      recipientAddress={recipientAddress}
                      occasion={initialOccasion}
                      signatureUrl={signatureUrl}
                      showDebug={false}
                      textStyle={{
                        font: FONT_OPTIONS[fontFamily],
                        alignment: textAlignment,
                        fontSize: FONT_SIZES[fontSize]
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Cost Section */}
              <div>
                <h3 className="text-lg font-medium text-brand-secondary mb-3">Cost</h3>
                <div className="bg-white rounded-lg border p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-brand-secondary opacity-75" />
                        <span>One-time Purchase</span>
                      </div>
                      <span className={isCardFree ? 'line-through text-gray-400' : ''}>
                        {singleCardPrice ? formatPrice(singleCardPrice.amount) : '$3.50'}
                      </span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between items-center font-semibold">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-brand-primary" />
                          <span>Total</span>
                        </div>
                        <span className="text-brand-primary">
                          {isCardFree ? 'FREE' : (singleCardPrice ? formatPrice(singleCardPrice.amount) : '$3.50')}
                        </span>
                      </div>
                      {hasAvailableCredits && (
                        <p className="text-sm text-green-600 mt-2">
                          Using 1 credit from your available credits ({freeCardsAvailable - cardsUsedThisMonth} remaining)
                        </p>
                      )}
                      {hasActiveSubscription && !hasAvailableCredits && (
                        <p className="text-sm text-green-600 mt-2">
                          Free with your {subscription?.price_id === 'price_1Qq2ecPcJBAkjOG46gNS7Qo5' ? 'Standard' : 'Premium'} subscription
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer with Navigation */}
        <div className="flex-shrink-0 border-t bg-white p-4">
          <div className="flex justify-between items-center max-w-4xl mx-auto w-full">
            <button
              onClick={() => currentStep === 'customize' ? onClose() : setCurrentStep('customize')}
              className="flex items-center gap-2 px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {currentStep === 'customize' ? 'Cancel' : 'Back'}
            </button>
            <button
              onClick={handleNextStep}
              disabled={isSubmitting}
              className={`px-6 py-2 rounded-lg transition-all ${
                isSubmitting
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-brand-primary text-white hover:bg-brand-secondary'
              }`}
            >
              {getActionButtonText()}
              {currentStep === 'preview' && !isCardFree && singleCardPrice && (
                <span> ({formatPrice(singleCardPrice.amount)})</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignatureSave}
          onClose={() => setShowSignaturePad(false)}
        />
      )}
    </div>
  );
}